import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Zap, Scissors, Heart, ArrowRight, Clock, Award, Users } from 'lucide-react';
import { servicesData } from '../data/mockData';

const iconMap = {
  Sparkles,
  Zap,
  Scissors,
  Heart
};

const Services = () => {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section" style={{ background: 'var(--warm-gradient)' }}>
        <div className="container text-center">
          <h1 className="mb-6 fade-in" style={{ color: 'var(--charcoal)' }}>
            Our Luxury Services
          </h1>
          <p className="text-xl max-w-3xl mx-auto fade-in-delay-1" style={{ color: 'var(--charcoal)' }}>
            Indulge in our comprehensive range of premium beauty treatments, 
            each designed to enhance your natural radiance and well-being.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {servicesData.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <div 
                  key={service.id}
                  className={`group flex flex-col md:flex-row bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 fade-in-delay-${index % 3 + 1}`}
                >
                  <div className="md:w-1/2 relative overflow-hidden">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-64 md:h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 p-3 rounded-full" style={{ backgroundColor: 'var(--champagne-gold)' }}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  
                  <div className="md:w-1/2 p-8 flex flex-col justify-center">
                    <h3 className="mb-4" style={{ color: 'var(--charcoal)' }}>
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {service.description}
                    </p>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center space-x-3">
                        <Clock className="w-5 h-5" style={{ color: 'var(--champagne-gold)' }} />
                        <span className="text-sm text-gray-600">60-90 minutes</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Award className="w-5 h-5" style={{ color: 'var(--champagne-gold)' }} />
                        <span className="text-sm text-gray-600">Professional certified</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Users className="w-5 h-5" style={{ color: 'var(--champagne-gold)' }} />
                        <span className="text-sm text-gray-600">Personalized consultation</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm text-gray-500">Starting from</span>
                        <div className="text-2xl font-bold" style={{ color: 'var(--champagne-gold)' }}>
                          {service.price}
                        </div>
                      </div>
                      <Link 
                        to="/contact" 
                        className="btn-primary"
                      >
                        <span>Book Now</span>
                        <ArrowRight size={18} />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section" style={{ background: 'var(--blush-gradient)' }}>
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="mb-4 fade-in" style={{ color: 'var(--charcoal)' }}>
              Why Choose Luxe Beauty
            </h2>
            <p className="text-lg max-w-2xl mx-auto fade-in-delay-1" style={{ color: 'var(--charcoal)' }}>
              Experience the difference with our commitment to excellence and personalized care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center fade-in-delay-1">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'var(--champagne-gold)' }}>
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="mb-3" style={{ color: 'var(--charcoal)' }}>
                Expert Professionals
              </h3>
              <p className="text-gray-600">
                Our team consists of certified experts with years of experience in luxury beauty treatments.
              </p>
            </div>

            <div className="text-center fade-in-delay-2">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'var(--champagne-gold)' }}>
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="mb-3" style={{ color: 'var(--charcoal)' }}>
                Premium Products
              </h3>
              <p className="text-gray-600">
                We use only the finest, clinically-proven products to ensure exceptional results.
              </p>
            </div>

            <div className="text-center fade-in-delay-3">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'var(--champagne-gold)' }}>
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="mb-3" style={{ color: 'var(--charcoal)' }}>
                Personalized Care
              </h3>
              <p className="text-gray-600">
                Every treatment is tailored to your unique needs and beauty goals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-charcoal text-white" style={{ backgroundColor: 'var(--charcoal)' }}>
        <div className="container text-center">
          <h2 className="mb-6 text-white fade-in">
            Ready to Experience Luxury?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto fade-in-delay-1">
            Book your consultation today and discover the perfect treatment for your needs.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6 fade-in-delay-2">
            <Link to="/contact" className="btn-primary">
              Schedule Consultation
            </Link>
            <Link to="/pricing" className="btn-secondary">
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;